import { Box, IconButton, Tooltip } from '@mui/material';
import { colors } from '@src/styles';
import { UserBox } from '../components/UserBox';
import { ScriptsDTO } from '@src/services/tools/modules/v1/scripts/dto/brain-scripts.getById.response.dto';
import { ReactNode } from 'react';
import ArrowOutwardIcon from '@mui/icons-material/ArrowOutward';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import SyncIcon from '@mui/icons-material/Sync';
import BlockIcon from '@mui/icons-material/Block';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import { SvgIconComponent } from '@mui/icons-material';
import { format } from 'date-fns';
import { monoFontFamily } from '../scriptsStyles';

const SCRIPT_STATUS_CONFIG: Record<string, { icon: SvgIconComponent; color: string; label: string }> = {
  PENDING: { icon: HourglassEmptyIcon, color: colors.brand.textSecondary, label: 'Pendente' },
  RUNNING: { icon: SyncIcon, color: colors.status.pending, label: 'Executando' },
  BLOCKED: { icon: BlockIcon, color: colors.status.rejected, label: 'Bloqueado' },
  FINISHED: { icon: CheckCircleIcon, color: colors.status.concluded, label: 'Finalizado' },
  FAILED: { icon: ErrorIcon, color: colors.primary.warning, label: 'Falhou' },
};

const dateCellSX = {
  fontFamily: monoFontFamily,
  fontSize: '12px',
  color: colors.brand.textSecondary,
  whiteSpace: 'nowrap',
} as const;

const ellipsisSX = { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } as const;

export const getScriptsListColumns = (
  userNames: Record<string, string>,
  isLoadingUsers: boolean,
  onScriptClick: (script: ScriptsDTO) => void,
  organizationNames: Record<string, string>,
) => {
  return [
    {
      label: 'Nome',
      key: 'name',
      align: 'left' as const,
      render: (row: ScriptsDTO) => (
        <Box title={row.name} sx={{ ...ellipsisSX, maxWidth: 380, fontWeight: 500, color: colors.primary.white }}>
          {row.name}
        </Box>
      ),
    },
    {
      label: 'Organização',
      key: 'organizationId',
      width: 200,
      align: 'left' as const,
      render: (row: ScriptsDTO) => {
        const organizationName = organizationNames[row.execution?.organizationId] ?? '-';
        return (
          <Box title={organizationName} sx={{ ...ellipsisSX, maxWidth: 200, color: colors.brand.textStrong }}>
            {organizationName}
          </Box>
        );
      },
    },
    {
      label: 'Criado em',
      key: 'createdAt',
      width: 104,
      align: 'left' as const,
      render: (row: ScriptsDTO) => <Box sx={dateCellSX}>{format(new Date(row.createdAt), 'dd/MM HH:mm')}</Box>,
    },
    {
      label: 'Disparado em',
      key: 'triggeredAt',
      width: 116,
      align: 'left' as const,
      render: (row: ScriptsDTO) => (
        <Box sx={dateCellSX}>
          {row.execution?.startedRunAt ? format(new Date(row.execution.startedRunAt), 'dd/MM HH:mm') : '—'}
        </Box>
      ),
    },
    {
      label: 'Criado por',
      key: 'createdByUserId',
      width: 160,
      align: 'left' as const,
      render: (row: ScriptsDTO) => (
        <UserBox
          label=""
          userId={row.createdByUserId}
          userNames={userNames}
          isLoadingUsers={isLoadingUsers}
          orientation="vertical"
        />
      ),
    },
    {
      label: 'Aprovado por',
      key: 'approvedByUserId',
      width: 160,
      align: 'left' as const,
      render: (row: ScriptsDTO) => (
        <UserBox
          label=""
          userId={row.approvedByUserId}
          userNames={userNames}
          isLoadingUsers={isLoadingUsers}
          orientation="vertical"
        />
      ),
    },
    {
      label: 'Status',
      key: 'status',
      width: 120,
      align: 'left' as const,
      render: (row: ScriptsDTO) => {
        const { icon: Icon, color, label } = SCRIPT_STATUS_CONFIG[row.execution?.status || 'PENDING'];
        return (
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.625,
              height: 22,
              px: 1,
              borderRadius: '999px',
              border: `1px solid ${color}55`,
              backgroundColor: `${color}14`,
              color,
              fontFamily: monoFontFamily,
              fontSize: '10px',
              letterSpacing: '0.1em',
              textTransform: 'uppercase',
              whiteSpace: 'nowrap',
            }}
          >
            <Icon sx={{ color: `${color} !important`, fontSize: '12px' }} />
            {label}
          </Box>
        );
      },
    },
    {
      label: '',
      key: 'details',
      width: 56,
      align: 'right' as const,
      render: (row): ReactNode => (
        <Tooltip title="Abrir script" arrow>
          <IconButton
            aria-label={`Abrir script ${row.name}`}
            onClick={(event) => {
              event.stopPropagation();
              onScriptClick(row);
            }}
            sx={{
              width: 30,
              height: 30,
              borderRadius: '8px',
              color: colors.brand.textSecondary,
              '& svg': { color: 'inherit', fontSize: 17 },
              '&:hover': { color: colors.primary.yellow, backgroundColor: 'rgba(255, 160, 21, 0.1)' },
            }}
          >
            <ArrowOutwardIcon />
          </IconButton>
        </Tooltip>
      ),
    },
  ];
};
